// FullQualityView — a material's policy evaluation, shown to the author as a
// calm report card grouped by quality axis, pure.
//
// This is the DETAIL surface: the roomy per-constraint drill-down. The resting
// right-panel view (`QualitySummary`) is now a flat, glanceable verdict — score
// + one line per axis + a "Show details" door. This component is what that door
// opens onto (the shell mounts it as its own tab); it has room to keep the rich
// axes → constraints → evidence drill-down that would clutter the resting panel.
//
// The Constraint/Policy quality model (`docs/model/constraints.md`) evaluates a
// material against a policy at a boundary and yields a `PolicyEvaluation`: a
// per-constraint `Bite` (pass / warn / block / pending) plus an aggregate
// report-card SCORE (the mean of the measured scores, kept separate from the
// block/warn decision — grade the value, enforce discretely).
//
// The view groups the flat per-constraint list into the THREE quality axes
// the product reasons in (`project_quality_focus`): FACTUAL (grounding + citation
// resolution), LINGUISTIC (readability), PEDAGOGICAL (the judged distractor
// rubric). The grouping is driven by a pure, exhaustive classification over the
// constraint KINDS (`axisOfKind`, a total switch): a new built-in constraint kind
// fails to compile until it is given an axis — there is no untyped bucket. Each
// axis drills down (native `<details>`, keyboard-operable, expansion exposed to
// AT) to its constituent constraints, and each constraint drills down again to
// the evidence the evaluation already holds (how it is checked, its force in this
// policy, its measured score, and how that produced its outcome) — nothing is
// invented.
//
// The locale-free folds it derives from (`groupByAxis`, `axisRoll`, `worstResult`,
// `axisVerdicts`) and the shared localised copy (`usePolicyLabels`) are exported so
// the flat resting panel renders the SAME verdict from the SAME source — the two
// surfaces (glance + detail) cannot disagree about an axis's status.
//
// Register follows the boundary (`docs/model/constraints.md` §Boundaries):
//   - at WRITE the enforcement is ADVISORY — a warning is guidance, not an
//     error, and NOTHING blocks the author. The report informs; it does not
//     gate. The hard gate lives at export (opt-in, OFF by default), so the
//     write view says so rather than pretending it can stop you.
//   - at an export GATE a `block` can appear — surfaced honestly, but only
//     there. The axis carrying the block is the one that reads "Blocks export";
//     the advisory axes stay calm (alarm discipline).
//
// The PEDAGOGICAL axis is `judged`: its verdict needs an evaluator (an LLM),
// which is not wired. It therefore rolls up honestly to "Not yet assessed" — a
// real pending state (`AxisRoll.all_pending`), never a fabricated score. The core
// never fakes the verdict, and neither does this panel.
//
// Colour is never the sole channel (WCAG SC 1.4.1) — every outcome pairs an icon
// with a word. Split for testability, the codebase idiom: the locale-free
// mappings (`constraintKind`, `axisOfKind`, `groupByAxis`, `axisRoll`, `biteLook`,
// `pct`) are pure exported functions the tests pin; the author-facing STRINGS live
// inside the component body where the Lingui `t` macro is in lexical scope. The
// evaluation arrives as a PROP; the component holds no state and does no I/O — the
// shell (the `evaluateMaterialPolicy` port) produces it, and native `<details>`
// owns the disclosure state, so no React state is needed.

export type {
    AxisRoll,
    AxisVerdictLine,
    BiteLook,
    BiteTone,
    ClassifiedKind,
    ConstraintKind,
    FullQualityViewProps,
    GroupedResults,
    MeasuredBite,
    PolicyLabels,
    QualityAxis,
};
export {
    axisHeadlineBite,
    axisOfKind,
    axisRoll,
    axisVerdicts,
    biteLook,
    constraintKind,
    FullQualityView,
    groupByAxis,
    outcomeBadge,
    pct,
    usePolicyLabels,
    worstResult,
};

import * as Core from "@bilda/core";
import { exhaustSwitchCase, Maybe } from "@bilda/utils";
import { Trans, useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Badge } from "../Badge/Badge.js";
import { HelpHint } from "../HelpHint/HelpHint.js";
import type { IconName } from "../Icon/Icon.js";
import { Icon } from "../Icon/Icon.js";
import { Text } from "../Text/Text.js";
import "./PolicyReport.css";

type FullQualityViewProps = {
    // The pure evaluation the core produced (material × policy × boundary).
    readonly evaluation: Core.Policy.Evaluate.PolicyEvaluation;
    // Names the region for AT (caller-owned / localised).
    readonly ariaLabel: string;
    readonly className?: string;
};

// === Constraint id → kind (locale-free) ===
//
// No raw ids ever reach the author (no dev jargon). The MVP policy is the
// built-in `qualityPolicy`; each member's id is matched against the exported
// constraint constants, so a renamed id can't silently leak through. `other` is
// the humane catch-all for a member we don't yet have copy for — never the id
// itself. The component maps this closed union to a localised label.
type ConstraintKind = "readability" | "coverage" | "distractors" | "other";

const constraintKind = (
    id: Core.Policy.Constraint.ConstraintId,
): ConstraintKind => {
    if (id === Core.Policy.linguisticReadability.id) return "readability";
    if (id === Core.Policy.citationCoverage.id) return "coverage";
    if (id === Core.Policy.distractorPlausibility.id) return "distractors";
    return "other";
};

// === Constraint kind → quality axis (locale-free) ===
//
// The three axes the product reasons in (`project_quality_focus`):
//   - `linguistic` — computed readability (LIX-type) signals.
//   - `factual`    — computed claim-backing (citation coverage) signals.
//   - `pedagogical`— judged signals (the distractor rubric).
//
// `ClassifiedKind` is EVERY constraint kind except the humane `other` catch-all.
// Deriving it from `ConstraintKind` means a NEW built-in kind automatically joins
// this union and breaks `axisOfKind`'s exhaustive switch until it is classified —
// the compile-time gate. `other` (a constraint we have no copy for) has no axis:
// `groupByAxis` surfaces it separately rather than forcing it into a wrong bucket.
type QualityAxis = "linguistic" | "factual" | "pedagogical";

type ClassifiedKind = Exclude<ConstraintKind, "other">;

const axisOfKind = (kind: ClassifiedKind): QualityAxis => {
    switch (kind) {
        case "readability":
            return "linguistic";
        case "coverage":
            return "factual";
        case "distractors":
            return "pedagogical";
        default:
            return exhaustSwitchCase(kind);
    }
};

// The stable display order — factual first (the "is it true?" question the
// audience cares about most), then language, then the pedagogical rubric.
const AXIS_ORDER: readonly QualityAxis[] = [
    "factual",
    "linguistic",
    "pedagogical",
];

// === Grouping (locale-free) ===

type AxisGroup = {
    readonly axis: QualityAxis;
    readonly results: readonly Core.Policy.Evaluate.PerConstraintResult[];
};

type GroupedResults = {
    // Always the three axes, in `AXIS_ORDER`, each with its members (possibly
    // none — an empty axis is a real, honestly-rendered state).
    readonly axes: readonly AxisGroup[];
    // Members whose kind is `other` — no axis fits, so they are shown on their
    // own rather than silently dropped or mis-filed.
    readonly unclassified: readonly Core.Policy.Evaluate.PerConstraintResult[];
};

const groupByAxis = (
    results: readonly Core.Policy.Evaluate.PerConstraintResult[],
): GroupedResults => {
    const buckets: Record<
        QualityAxis,
        Core.Policy.Evaluate.PerConstraintResult[]
    > = { factual: [], linguistic: [], pedagogical: [] };
    const unclassified: Core.Policy.Evaluate.PerConstraintResult[] = [];
    for (const r of results) {
        const kind = constraintKind(r.constraintId);
        if (kind === "other") {
            unclassified.push(r);
            continue;
        }
        buckets[axisOfKind(kind)].push(r);
    }
    return {
        axes: AXIS_ORDER.map((axis) => ({ axis, results: buckets[axis] })),
        unclassified,
    };
};

// === Axis rollup (locale-free) ===
//
// The honest one-line status for a whole axis:
//   - `empty`       — no constraints on this axis in this policy.
//   - `all_pending` — every constraint is unverified (the judged pedagogical
//                     axis lands here until an evaluator is wired): "Not yet
//                     assessed", a real state, never a faked score.
//   - `measured`    — at least one verdict exists; `headline` is the MOST-SEVERE
//                     bite present (block > warn > pass), so the axis reads
//                     "Blocks export" iff it actually carries a block; `hasPending`
//                     flags that some of its constraints are still unverified.
type MeasuredBite = Exclude<Core.Policy.Evaluate.Bite, "pending">;

type AxisRoll =
    | { readonly type: "empty" }
    | { readonly type: "all_pending" }
    | {
          readonly type: "measured";
          readonly headline: MeasuredBite;
          readonly hasPending: boolean;
      };

const axisRoll = (
    results: readonly Core.Policy.Evaluate.PerConstraintResult[],
): AxisRoll => {
    if (results.length === 0) return { type: "empty" };
    const measured = results.filter((r) => r.bite !== "pending");
    if (measured.length === 0) return { type: "all_pending" };
    const headline: MeasuredBite = measured.some((r) => r.bite === "block")
        ? "block"
        : measured.some((r) => r.bite === "warn")
          ? "warn"
          : "pass";
    return {
        type: "measured",
        headline,
        hasPending: measured.length < results.length,
    };
};

// === Worst issue per axis (locale-free) ===
//
// The flat resting panel shows "at most the single worst issue" for an axis. That
// is the MOST-SEVERE measured result (block > warn > pass); a pending result is
// not an issue, so it is skipped. `null` when nothing is measured (an empty or
// all-pending axis has no issue to name). The severity order matches `axisRoll`'s
// headline, so the worst issue always belongs to the bite the rollup badge shows.
const severityRank = (bite: MeasuredBite): number => {
    const ranks: Record<MeasuredBite, number> = { pass: 0, warn: 1, block: 2 };
    return ranks[bite];
};

const worstResult = (
    results: readonly Core.Policy.Evaluate.PerConstraintResult[],
): Core.Policy.Evaluate.PerConstraintResult | null => {
    let worst: Core.Policy.Evaluate.PerConstraintResult | null = null;
    let worstRank = -1;
    for (const r of results) {
        const bite = r.bite;
        if (bite === "pending") continue;
        const rank = severityRank(bite);
        if (rank > worstRank) {
            worstRank = rank;
            worst = r;
        }
    }
    return worst;
};

// === Per-axis verdict lines (locale-free) ===
//
// The flat resting panel's one-line-per-axis view: each of the three axes with
// its honest rollup and its single worst issue (if any). Derived from the SAME
// `groupByAxis` + `axisRoll` the detail view uses, so glance and detail agree.
type AxisVerdictLine = {
    readonly axis: QualityAxis;
    readonly roll: AxisRoll;
    readonly worst: Core.Policy.Evaluate.PerConstraintResult | null;
};

const axisVerdicts = (
    evaluation: Core.Policy.Evaluate.PolicyEvaluation,
): readonly AxisVerdictLine[] =>
    groupByAxis(evaluation.results).axes.map((group) => ({
        axis: group.axis,
        roll: axisRoll(group.results),
        worst: worstResult(group.results),
    }));

// === Bite → look (locale-free) ===
//
// The one place the `Bite` sum turns into an icon + tone, total and exhaustive
// so a new bite breaks compilation. Tone is calm by design: `warn` is the quiet
// warning tone (an advisory), NOT the danger tone — that is reserved for
// `block`, the only outcome that actually refuses a passage (and only at an
// export gate). `pending` is neutral: honest "not yet", never a faked verdict.
type BiteTone = "success" | "warning" | "danger" | "neutral";
type BiteLook = { readonly icon: IconName; readonly tone: BiteTone };

const biteLook = (bite: Core.Policy.Evaluate.Bite): BiteLook => {
    switch (bite) {
        case "pass":
            return { icon: "check", tone: "success" };
        case "warn":
            return { icon: "warning", tone: "warning" };
        case "block":
            return { icon: "danger", tone: "danger" };
        case "pending":
            return { icon: "clock", tone: "neutral" };
        default:
            return exhaustSwitchCase(bite);
    }
};

// A measured score rendered as a calm percentage, or `null` while pending.
const pct = (score: Maybe.Maybe<Core.Policy.Score.Score>): number | null =>
    Maybe.isSome(score)
        ? Math.round(Core.Policy.Score.from(score.value) * 100)
        : null;

// The outcome badge: `biteLook` picks the icon + tone (single source of truth),
// and the switch on that tone hands `Badge` a LITERAL tone so its
// status/label-tone discriminated union resolves without a cast. The localised
// `word` is supplied by the caller (component scope owns the macro).
const outcomeBadge = (
    bite: Core.Policy.Evaluate.Bite,
    word: string,
): ReactNode => {
    const { icon, tone } = biteLook(bite);
    const glyph = <Icon name={icon} size="xs" />;
    switch (tone) {
        case "success":
            return (
                <Badge tone="success" icon={glyph}>
                    {word}
                </Badge>
            );
        case "warning":
            return (
                <Badge tone="warning" icon={glyph}>
                    {word}
                </Badge>
            );
        case "danger":
            return (
                <Badge tone="danger" icon={glyph}>
                    {word}
                </Badge>
            );
        case "neutral":
            return (
                <Badge tone="neutral" icon={glyph}>
                    {word}
                </Badge>
            );
        default:
            return exhaustSwitchCase(tone);
    }
};

// The bite an axis rollup wears on its headline badge. `all_pending` reads as the
// neutral pending bite; a measured axis wears its most-severe bite; an empty axis
// has no headline (`null`).
const axisHeadlineBite = (roll: AxisRoll): Core.Policy.Evaluate.Bite | null => {
    switch (roll.type) {
        case "empty":
            return null;
        case "all_pending":
            return "pending";
        case "measured":
            return roll.headline;
        default:
            return exhaustSwitchCase(roll);
    }
};

// === Shared localised copy (the SINGLE source both surfaces read) ===
//
// The resting panel (glance) and this detail view render the same axis labels and
// verdict words. A hook — not two local record sets — keeps them from drifting:
// one surface saying "Advisory" while the other says "Warning" for the same bite
// is exactly the glance-vs-detail contradiction this file exists to prevent.
type PolicyLabels = {
    readonly kindLabels: Record<ConstraintKind, string>;
    readonly axisLabels: Record<QualityAxis, string>;
    readonly biteWords: Record<Core.Policy.Evaluate.Bite, string>;
    readonly productionWords: Record<Core.Policy.Constraint.Production, string>;
    readonly severityWords: Record<Core.Policy.Policy.Severity, string>;
    readonly outcomeExplain: Record<Core.Policy.Evaluate.Bite, string>;
    // The axis-level word for a pending axis is distinct from the per-constraint
    // "Not yet checked": at the axis it reads "Not yet assessed" (the judged
    // pedagogical axis's honest resting state).
    readonly axisWord: (bite: Core.Policy.Evaluate.Bite) => string;
};

const usePolicyLabels = (): PolicyLabels => {
    const { t } = useLingui();

    // Kind → author-facing label. A Record forces a label for every kind, so a
    // new kind breaks compilation until its copy is written.
    const kindLabels: Record<ConstraintKind, string> = {
        readability: t`Readability`,
        coverage: t`Every item is backed by your uploaded sources`,
        distractors: t`Plausible wrong answers`,
        other: t`Quality constraint`,
    };

    // Axis → author-facing, de-jargoned label. A Record forces copy for every
    // axis, so a new axis breaks compilation until it is named.
    const axisLabels: Record<QualityAxis, string> = {
        factual: t`Facts & sources`,
        linguistic: t`Language`,
        pedagogical: t`Teaching quality`,
    };

    // Bite → the word that carries the outcome on its own (colour is only
    // reinforcement). A Record forces copy for every bite.
    const biteWords: Record<Core.Policy.Evaluate.Bite, string> = {
        pass: t`Meets`,
        warn: t`Advisory`,
        block: t`Blocks export`,
        pending: t`Not yet checked`,
    };

    // How a constraint's verdict is produced — the pure-core line, in plain words.
    const productionWords: Record<Core.Policy.Constraint.Production, string> = {
        computed: t`Checked automatically from the material`,
        judged: t`Needs an evaluator's judgement`,
    };

    // A constraint's force WITHIN this policy — kept separate from the boundary.
    const severityWords: Record<Core.Policy.Policy.Severity, string> = {
        hard: t`Must-fix — can block an export`,
        soft: t`Advice — never blocks an export`,
    };

    // How the bite followed from the verdict, in plain words.
    const outcomeExplain: Record<Core.Policy.Evaluate.Bite, string> = {
        pass: t`Meets this check.`,
        warn: t`Falls short — shown as advice.`,
        block: t`Falls short — and the export gate is blocking on it.`,
        pending: t`Not assessed yet — this needs an evaluator, and none has run.`,
    };

    const axisWord = (bite: Core.Policy.Evaluate.Bite): string =>
        bite === "pending" ? t`Not yet assessed` : biteWords[bite];

    return {
        kindLabels,
        axisLabels,
        biteWords,
        productionWords,
        severityWords,
        outcomeExplain,
        axisWord,
    };
};

const FullQualityView = ({
    evaluation,
    ariaLabel,
    className,
}: FullQualityViewProps): ReactNode => {
    const { t } = useLingui();
    const {
        kindLabels,
        biteWords,
        axisLabels,
        productionWords,
        severityWords,
        outcomeExplain,
        axisWord,
    } = usePolicyLabels();

    // The report card: the mean of the MEASURED scores, as a calm 0–100. Kept
    // separate from enforcement — a number to read, not a pass/fail to alarm on.
    const overall = pct(evaluation.score);

    // Register line — the whole point of the write view: it informs, it does
    // not gate. Derived from the evaluation's own enforcement so the copy can
    // never drift from what the boundary actually does.
    const advisory = evaluation.enforcement === "advisory";

    const grouped = groupByAxis(evaluation.results);

    // One constraint: a collapsed `<summary>` (label + measured value + outcome
    // badge) that drills down to the evidence the evaluation already holds.
    const constraintRow = (
        r: Core.Policy.Evaluate.PerConstraintResult,
    ): ReactNode => {
        const value = pct(r.score);
        return (
            <li
                key={Core.Policy.Constraint.from(r.constraintId)}
                className="bilda-policy-report__row"
            >
                <details className="bilda-policy-report__constraint">
                    <summary className="bilda-policy-report__constraint-summary">
                        <Icon
                            name="chevron-right"
                            size="xs"
                            className="bilda-policy-report__disclosure"
                        />
                        <Text as="span" size="sm">
                            {kindLabels[constraintKind(r.constraintId)]}
                        </Text>
                        <span className="bilda-policy-report__row-end">
                            {value !== null ? (
                                <Text as="span" size="sm" tone="dim">
                                    {t`${value}%`}
                                </Text>
                            ) : null}
                            {outcomeBadge(r.bite, biteWords[r.bite])}
                        </span>
                    </summary>
                    <dl className="bilda-policy-report__evidence">
                        <div className="bilda-policy-report__fact">
                            <dt>{t`How it's checked`}</dt>
                            <dd>{productionWords[r.production]}</dd>
                        </div>
                        <div className="bilda-policy-report__fact">
                            <dt>{t`In this policy`}</dt>
                            <dd>{severityWords[r.severity]}</dd>
                        </div>
                        {value !== null ? (
                            <div className="bilda-policy-report__fact">
                                <dt>{t`Measured`}</dt>
                                <dd>{t`${value} out of 100`}</dd>
                            </div>
                        ) : null}
                        <div className="bilda-policy-report__fact">
                            <dt>{t`Outcome`}</dt>
                            <dd>{outcomeExplain[r.bite]}</dd>
                        </div>
                    </dl>
                </details>
            </li>
        );
    };

    // One axis: a headline (de-jargoned label + rollup badge) that drills down to
    // its constituent constraints. An empty axis renders honestly with no badge.
    const axisSection = (group: AxisGroup): ReactNode => {
        const roll = axisRoll(group.results);
        const headline = axisHeadlineBite(roll);
        return (
            <details
                key={group.axis}
                className="bilda-policy-report__axis"
                open={group.results.length > 0}
            >
                <summary className="bilda-policy-report__axis-summary">
                    <Icon
                        name="chevron-right"
                        size="xs"
                        className="bilda-policy-report__disclosure"
                    />
                    <Text as="span" size="sm" weight="medium">
                        {axisLabels[group.axis]}
                    </Text>
                    <span className="bilda-policy-report__row-end">
                        {roll.type === "measured" && roll.hasPending ? (
                            <Text as="span" size="sm" tone="dim">
                                {t`some not yet assessed`}
                            </Text>
                        ) : null}
                        {headline !== null ? (
                            outcomeBadge(headline, axisWord(headline))
                        ) : (
                            <Text as="span" size="sm" tone="dim">
                                {t`No checks in this policy`}
                            </Text>
                        )}
                    </span>
                </summary>
                {group.results.length > 0 ? (
                    <ul className="bilda-policy-report__list">
                        {group.results.map(constraintRow)}
                    </ul>
                ) : null}
            </details>
        );
    };

    return (
        <section
            aria-label={ariaLabel}
            className={`bilda-policy-report${className ? ` ${className}` : ""}`}
        >
            <div className="bilda-policy-report__head">
                <div className="bilda-policy-report__score">
                    {overall !== null ? (
                        <>
                            <Text
                                as="span"
                                size="2xl"
                                weight="semibold"
                                className="bilda-policy-report__score-value"
                            >
                                {overall}
                            </Text>
                            <Text as="span" size="sm" tone="dim">
                                {t`/100`}
                            </Text>
                        </>
                    ) : (
                        <Text size="sm" tone="dim">
                            {t`Not yet measured`}
                        </Text>
                    )}
                </div>
                <div className="bilda-policy-report__score-label">
                    <Text as="div" size="sm" weight="medium" tone="dim">
                        {t`Quality score`}
                    </Text>
                    <HelpHint label={t`Explanation: quality score`}>
                        <Trans>
                            The average of the checks that could be measured. It
                            is a reading of the material's quality – separate
                            from what blocks an export.
                        </Trans>
                    </HelpHint>
                </div>
            </div>

            <Text size="sm" tone="dim">
                {advisory
                    ? t`These are advisories — nothing here blocks your work. Quality is enforced only at export, and only if you turn the gate on.`
                    : t`The export gate is on — anything marked "blocks export" must be resolved before this material can be exported.`}
            </Text>

            <div className="bilda-policy-report__axes">
                {grouped.axes.map(axisSection)}
                {grouped.unclassified.length > 0 ? (
                    <details className="bilda-policy-report__axis" open>
                        <summary className="bilda-policy-report__axis-summary">
                            <Icon
                                name="chevron-right"
                                size="xs"
                                className="bilda-policy-report__disclosure"
                            />
                            <Text as="span" size="sm" weight="medium">
                                {t`Other checks`}
                            </Text>
                        </summary>
                        <ul className="bilda-policy-report__list">
                            {grouped.unclassified.map(constraintRow)}
                        </ul>
                    </details>
                ) : null}
            </div>
        </section>
    );
};
